import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useToast } from '../../../contexts/ToastContext';
import { useAnalytics } from '../../../hooks/useAnalytics';
import Sidebar from '../../../components/Sidebar';
import Settings from '../../../Settings';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/cjs/styles/prism';
import { FaPlus, FaSpinner, FaTrash, FaPaperclip, FaPaperPlane, FaDatabase } from 'react-icons/fa';
import { TbLayoutSidebarLeftCollapse, TbLayoutSidebarRightCollapse } from 'react-icons/tb';
import { FormControl, FormControlLabel, RadioGroup, Radio, Popover, Button as MUIButton} from '@mui/material';
import veloxIcon from '../../../assets/Velox-icon-color.png';
import { sendMessage, getChatMessages, deleteChat } from '../../../services/chat';
import { useVirtualChatHistory } from '../../../hooks/useVirtualChatHistory';
import VirtualScrollList from '../../../components/VirtualScrollList';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { fileToBase64 } from '../../../utils/fileHelpers';
import SlackAction from '../../../components/SlackAction';

// Module Pages
import CompassView from './CompassView';
import KnowledgeHub from './KnowledgeHub';
import Radar from './Radar';

// App styles
import '../../../App.css';


// Individual Plan Dashboard
function AgentVIndividualDashboard() {
  const { logout } = useAuth();
  
  const handleLogout = () => {
    logout();
  };

  return (
    <div className="app-root">
      <Sidebar 
        onLogout={handleLogout} 
      />
      <main className="main-content">
        
        <Routes>
          <Route index element={<Navigate to="agent-v" replace />} />
          <Route path="agent-v" element={<AgentVIndividualAssistant />} />
          <Route path="compass" element={<CompassView />} />
          <Route path="compass/*" element={<CompassView />} />
          <Route path="knowledge" element={<Navigate to="knowledge-hub" replace />} />
          <Route path="knowledge-hub" element={<KnowledgeHub />} />
          <Route path="knowledge-hub/*" element={<KnowledgeHub />} />
          <Route path="radar/*" element={<Radar />} />
          <Route path="settings" element={<Settings />} />
          {/* Remove the problematic catch-all route that was causing redirects */}
        </Routes>
      </main>
    </div>
  );
}

// Individual Plan Agent V
function AgentVIndividualAssistant() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [selectedChat, setSelectedChat] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [showHistory, setShowHistory] = useState(true);
  const { user } = useAuth();
  const { trackFeatureUsage, trackModuleAccess } = useAnalytics();
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [streamingMessage, setStreamingMessage] = useState(null);
  const { showError } = useToast();
  
  // Add new state for pagination and caching
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const messagesCache = useRef(new Map());
  const lastLoadedChatId = useRef(null); // Track which chat we last loaded messages for
  const PAGE_SIZE = 20;
  const MESSAGE_CACHE_TIME = 2 * 60 * 1000; // 2 minutes

  // Add new state for chat loading
  const [isChatLoading, setIsChatLoading] = useState(false);

  // Add state for communication style, persisted in sessionStorage
  const getInitialCommStyle = () => sessionStorage.getItem('commStyle') || 'professional';
  const [commStyle, setCommStyle] = useState(getInitialCommStyle());
  const [commAnchorEl, setCommAnchorEl] = useState(null);

  // Use virtual chat history hook
  const {
    allChats: chatHistory,
    isLoading: isLoadingHistory,
    isLoadingMore: isLoadingMoreChats,
    error: historyError,
    hasMore: hasMoreChats,
    loadInitialChatHistory,
    loadMoreChats,
    addNewChat,
    removeChat,
    refreshChatHistory
  } = useVirtualChatHistory(user?.id);

  useEffect(() => {
    sessionStorage.setItem('commStyle', commStyle);
  }, [commStyle]);

  // Show toast notification when history error occurs
  useEffect(() => {
    if (historyError) {
      showError(historyError);
    }
  }, [historyError, showError]);

  const commOptions = [
    { value: 'professional', label: 'Professional - Formal and business-oriented' },
    { value: 'casual', label: 'Casual - Friendly and conversational' },
  ];

  // Add function to check if cache is valid for messages
  const isCacheValid = (timestamp, cacheTime) => {
    return timestamp && (Date.now() - timestamp < cacheTime);
  };

  // Update loadMoreMessages with better caching
  const loadMoreMessages = useCallback(async () => {
    if (!selectedChat || isLoadingMore || !hasMoreMessages) return;
    if (messages.length === 0) return; // Don't try to load more if there are no messages
    setIsLoadingMore(true);
    try {
      const oldestMessageTimestamp = messages.length > 0 
        ? messages[messages.length - 1].timestamp 
        : new Date().toISOString();
      const cacheKey = `${selectedChat.id}-${oldestMessageTimestamp}`;
      const cachedData = messagesCache.current.get(cacheKey);
      if (cachedData && isCacheValid(cachedData.timestamp, MESSAGE_CACHE_TIME)) {
        setMessages(prev => [...prev, ...cachedData.data]);
        setHasMoreMessages(cachedData.data.length === PAGE_SIZE);
        setIsLoadingMore(false);
        return;
      }
      const response = await getChatMessages(selectedChat.id, {
        before: oldestMessageTimestamp,
        limit: PAGE_SIZE
      });
      if (Array.isArray(response)) {
        const newMessages = response;
        messagesCache.current.set(cacheKey, {
          data: newMessages,
          timestamp: Date.now()
        });
        setMessages(prev => [...prev, ...newMessages]);
        setHasMoreMessages(newMessages.length === PAGE_SIZE);
      } else {
        setHasMoreMessages(false);
      }
    } catch (error) {
      // Only show error if user explicitly tried to load more (not on initial load or empty)
      if (messages.length > 0) {
        showError('Failed to load more messages. Please try again.');
      }
    } finally {
      setIsLoadingMore(false);
    }
  }, [selectedChat, messages, isLoadingMore, hasMoreMessages, MESSAGE_CACHE_TIME]);

  // Load initial chat history
  useEffect(() => {
    if (user) {
      loadInitialChatHistory();
    }
  }, [user, loadInitialChatHistory]);

  // Add scroll handler for infinite loading
  const handleScroll = useCallback(() => {
    if (!chatContainerRef.current) return;
    if (!hasMoreMessages || isLoadingMore) return; // Prevent auto-load if no more messages or already loading
    const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
    // Load more when user scrolls to 80% of the content
    if (scrollTop + clientHeight >= scrollHeight * 0.8) {
      loadMoreMessages();
    }
  }, [isLoadingMore, hasMoreMessages, loadMoreMessages]);

  // Add scroll event listener
  useEffect(() => {
    const container = chatContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  // Update message loading logic with caching
  useEffect(() => {
    if (selectedChat && selectedChat.id) {
      // Don't load if we already loaded messages for this chat
      if (lastLoadedChatId.current === selectedChat.id) {
        // We already have messages for this chat, don't reload
        return;
      }
      
      // Don't load messages if we already have messages and they're recent (active conversation)
      if (messages.length > 0) {
        const mostRecentMessage = messages[messages.length - 1];
        if (mostRecentMessage && mostRecentMessage.timestamp) {
          const messageTime = new Date(mostRecentMessage.timestamp).getTime();
          const now = Date.now();
          // If messages are very recent (within 5 seconds), we're in an active conversation
          // Don't show loading, just continue with existing messages
          if (now - messageTime < 5000) {
            lastLoadedChatId.current = selectedChat.id;
            return; // Skip loading, we already have the messages
          }
        }
      }
      
      const loadMessages = async () => {
        setIsChatLoading(true);
        try {
          const cacheKey = `${selectedChat.id}-initial`;
          const cachedMessages = messagesCache.current.get(cacheKey);
          if (cachedMessages) {
            setMessages(cachedMessages);
            lastLoadedChatId.current = selectedChat.id;
            setIsChatLoading(false);
            return;
          }
          const response = await getChatMessages(selectedChat.id, { limit: PAGE_SIZE });
          if (Array.isArray(response)) {
            messagesCache.current.set(cacheKey, response);
            setMessages(response);
            lastLoadedChatId.current = selectedChat.id;
            setHasMoreMessages(response.length === PAGE_SIZE);
          } else {
            setMessages([]);
            setHasMoreMessages(false);
            lastLoadedChatId.current = selectedChat.id;
          }
        } catch (error) {
          showError('Failed to load messages');
          setMessages([]);
          setHasMoreMessages(false);
          lastLoadedChatId.current = selectedChat.id;
        } finally {
          setIsChatLoading(false);
        }
      };
      
      // Only clear messages if we're switching to a different chat
      if (lastLoadedChatId.current && lastLoadedChatId.current !== selectedChat.id) {
        setMessages([]);
      }
      setHasMoreMessages(true);
      loadMessages();
    } else if (selectedChat === null) {
      // Clear messages when no chat is selected
      setMessages([]);
      setHasMoreMessages(false);
      lastLoadedChatId.current = null;
    }
  }, [selectedChat?.id]);

  // Add useEffect to handle initial sidebar state
  useEffect(() => {
    // Set initial state based on screen width
    setShowHistory(window.innerWidth > 768);
  }, []);

  // Add window resize handler
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setShowHistory(true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const createNewChat = () => {
    setSelectedChat(null);
    setMessages([]);
    setAttachments([]);
    lastLoadedChatId.current = null; // Reset when creating new chat
  };

  const handleFileAttachment = (event) => {
    const files = Array.from(event.target.files);
    
    // Track file upload
    files.forEach(file => {
      trackFeatureUsage('file_upload', {
        plan: 'individual',
        file_type: file.type,
        file_size: file.size,
        file_name: file.name
      });
    });
    
    setAttachments(prev => [...prev, ...files]);
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() && attachments.length === 0) return;

    // Track chat interaction
    trackFeatureUsage('chat_message', {
      plan: 'individual',
      has_attachments: attachments.length > 0,
      message_length: input.length,
      is_new_chat: !selectedChat
    });

    const displayAttachments = attachments.map(file => ({
      name: file.name,
      size: file.size,
      type: file.type
    }));

    const preparedAttachments = await Promise.all(
      attachments.map(async (file) => ({
        name: file.name,
        type: file.type || 'application/octet-stream',
        size: file.size,
        content: await fileToBase64(file)
      }))
    );

    const userMessage = {
      content: input,
      type: 'user',
      attachments: displayAttachments,
      timestamp: new Date().toISOString(),
      status: 'sending'
    };

    setMessages(prev => [...prev, userMessage]);
    const streamingTimestamp = new Date().toISOString();
    setStreamingMessage({
      content: '',
      type: 'assistant',
      timestamp: streamingTimestamp,
      status: 'streaming'
    });
    const currentInput = input;
    setInput('');
    setAttachments([]);
    setIsLoading(true);

    try {
      const response = await sendMessage(currentInput, selectedChat?.id, commStyle, {
        stream: true,
        attachments: preparedAttachments,
        onToken: (token) => {
          setStreamingMessage(prev => (
            prev ? { ...prev, content: `${prev.content || ''}${token}` } : prev
          ));
        }
      });
      if (!response || !response.chat || !response.message) {
        throw new Error('Invalid response structure from server');
      }
      const { chat, message } = response;
      
      // Update chat history
      if (!selectedChat) {
        addNewChat(chat);
        // Mark this chat as loaded so we don't trigger loading
        lastLoadedChatId.current = chat.id;
        setSelectedChat(chat);
        // Don't trigger message loading for new chats - we already have the messages
      } else {
        // Update existing chat in the list - this will be handled by the hook
        // The hook manages the chat list state
        // Mark as loaded if it's the same chat
        if (selectedChat.id === chat.id) {
          lastLoadedChatId.current = chat.id;
        }
      }

      // Update message status
      setMessages(prev => prev.map(msg => 
        msg.timestamp === userMessage.timestamp && msg.content === userMessage.content
          ? { ...msg, status: 'sent' }
          : msg
      ));

      // Use streaming content if available to ensure continuity
      // This prevents visual refresh by ensuring smooth transition
      const finalMessageContent = streamingMessage?.content && streamingMessage.content.length > 0
        ? streamingMessage.content
        : message.content;

      // Add final message first, then clear streaming message
      // React 18+ automatically batches these updates in event handlers
      setMessages(prev => [...prev, { 
        ...message, 
        content: finalMessageContent,
        status: 'sent',
        timestamp: message.timestamp || streamingTimestamp
      }]);
      setStreamingMessage(null);
    } catch (error) {
      console.error('Error sending message:', error);
      showError(error.message || 'Failed to send message. Please try again.');
      
      // Update message status to failed (but don't show error in chat)
      setMessages(prev => prev.map(msg => 
        msg.timestamp === userMessage.timestamp && msg.content === userMessage.content
          ? { ...msg, status: 'failed' }
          : msg
      ));
      setStreamingMessage(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteChat = async (chatId) => {
    try {
      await deleteChat(chatId);
      removeChat(chatId);
      if (selectedChat?.id === chatId) {
        setSelectedChat(null);
        setMessages([]);
      }
    } catch (error) {
      console.error('Error deleting chat:', error);
      showError('Failed to delete chat');
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Just now';
    const date = new Date(timestamp);
    return Number.isNaN(date.getTime()) ? 'Just now' : date.toLocaleTimeString();
  };

  const renderMessage = (message, index) => {
    const messageKey = message.id || `${message.timestamp || 'pending'}-${message.type || 'message'}-${index}`;
    const messageClass = `chat-message ${message.type} ${message.status === 'sending' ? 'sending' : ''}`;

  return (
      <div key={messageKey} className={messageClass}>
        <div className="message-content">
          {message.content && (
            <div className="message-text">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  code({node, inline, className, children, ...props}) {
                    const match = /language-(\w+)/.exec(className || '');
                    return !inline && match ? (
                      <SyntaxHighlighter
                        style={vscDarkPlus}
                        language={match[1]}
                        PreTag="div"
                        {...props}
                      >
                        {String(children).replace(/\n$/, '')}
                      </SyntaxHighlighter>
                    ) : (
                      <code className={className} {...props}>
                        {children}
                      </code>
                    );
                  }
                }}
              >
                {message.content}
              </ReactMarkdown>
            </div>
          )}
          {message.status === 'streaming' && !message.content && (
            <div className="typing-indicator">
              <span></span>
              <span></span>
              <span></span>
            </div>
          )}
          
          {message.attachments && message.attachments.length > 0 && (
            <div className="message-attachments">
              {message.attachments.map((attachment, index) => (
                <div key={index} className="attachment-item">
                  <FaPaperclip />
                  <span>{attachment.name}</span>
                  <span className="attachment-size">
                    {(attachment.size / 1024).toFixed(1)}KB
                  </span>
                </div>
              ))}
            </div>
          )}

          {message.sources && message.sources.length > 0 && (
            <div className="message-sources">
              <div className="sources-header">
                <FaDatabase /> Sources:
              </div>
              <ul>
                {message.sources.map((source, index) => (
                  <li key={index}>{source}</li>
                ))}
        </ul>
            </div>
          )}

          {message.type === 'assistant' && (
            <SlackAction
              messageContent={message.content}
              messageId={message.id}
              hasBeenSent={message.slackSent || false}
              allMessages={messages}
              onSent={(channelName) => {
                // Mark the message as sent
                setMessages(prev => prev.map(msg => 
                  msg.id === message.id || msg.timestamp === message.timestamp
                    ? { ...msg, slackSent: true }
                    : msg
                ));
                // Add a confirmation message
                const confirmationMessage = {
                  content: `Content has been shared to #${channelName} on Slack.`,
                  type: 'assistant',
                  timestamp: new Date().toISOString(),
                  status: 'sent',
                  slackSent: true // Mark confirmation message so button doesn't show
                };
                setMessages(prev => [...prev, confirmationMessage]);
              }}
            />
          )}
          
          <div className="message-timestamp">
            {formatTimestamp(message.timestamp)}
          </div>
        </div>
      </div>
    );
  };

  const renderWelcomeMessage = () => {
    if (messages.length > 0) return null;
    
    return (
      <div className="welcome-overlay">
        <div className="welcome-message">
          <h2>Hi, {user?.firstName || 'there'}!</h2>
          <p>What would you like to do?</p>
        </div>
      </div>
    );
  };

  const toggleHistory = () => {
    setShowHistory(prev => !prev);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  return (
    <div className="chat-layout">
      <div className="chat-container">
        <div className="chat-header">
          <div className="chat-title" style={{ justifyContent: 'flex-start', marginLeft: 0, textAlign: 'left', flex: 1 }}>
            <img src={veloxIcon} alt="Velox AI" className="chat-icon" />
            <span>Agent V</span>
          </div>
          <button 
            className="toggle-history-button"
            onClick={toggleHistory}
            aria-label={showHistory ? 'Hide chat history' : 'Show chat history'}
          >
            {showHistory ? <TbLayoutSidebarRightCollapse /> : <TbLayoutSidebarLeftCollapse color="#2563eb" />}
          </button>
        </div>
  
        <div className="chat-messages" ref={chatContainerRef}>
          {isLoadingMore && (
            <div className="loading-more-messages">
              <FaSpinner className="spinner" /> Loading more messages...
            </div>
          )}
          {isChatLoading && (
            <div className="loading-indicator">
              <FaSpinner className="spinner" /> Loading chat...
            </div>
          )}
          {!isChatLoading && renderWelcomeMessage()}
          {messages.map((message, index) => (
            renderMessage(message, index)
          ))}
          {streamingMessage && renderMessage(streamingMessage, messages.length)}
          {isLoading && !streamingMessage && (
            <div className="message assistant">
              <div className="message-content">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
  
        <div className="chat-input-container">
          {attachments.length > 0 && (
            <div className="attachments-preview">
              {attachments.map((file, index) => (
                <div key={index} className="attachment-preview-item">
                  <FaPaperclip />
                  <span>{file.name}</span>
                  <button
                    className="remove-attachment"
                    onClick={() => removeAttachment(index)}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
          
          <div className="chat-input-wrapper">
            <button 
              className="chat-input-avatar"
              onClick={() => fileInputRef.current?.click()}
              aria-label="Attach file"
            >
              <FaPaperclip />
            </button>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileAttachment}
              style={{ display: 'none' }}
              multiple
            />
            <MUIButton
              variant="outlined"
              size="small" 
              sx={{ ml: 1, mr: 1, fontSize: '0.65rem', textTransform: 'none', borderRadius: 2, minWidth: 0, px: 1.5, py: 1 }}
              onClick={e => setCommAnchorEl(e.currentTarget)}
              aria-label="Select communication style"
            >
              {commOptions.find(opt => opt.value === commStyle)?.label.split(' - ')[0]}
            </MUIButton>
            <Popover
              open={Boolean(commAnchorEl)}
              anchorEl={commAnchorEl}
              onClose={() => setCommAnchorEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              PaperProps={{ sx: { p: 2, minWidth: 260 } }}
            >
              <FormControl component="fieldset">
                <RadioGroup
                  aria-label="communication-style"
                  name="communication-style"
                  value={commStyle}
                  onChange={e => {
                    setCommStyle(e.target.value);
                    setCommAnchorEl(null);
                  }}
                >
                  {commOptions.map(opt => (
                    <FormControlLabel
                      key={opt.value}
                      value={opt.value}
                      control={<Radio size="small" />}
                      label={<span style={{ fontSize: 13 }}>{opt.label}</span>}
                      sx={{ mb: 1 }}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            </Popover>
            <textarea
              className="chat-input-field"
              placeholder="Type your message..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={1}
              aria-label="Message input"
            />
            <button 
              className="chat-input-send"
              onClick={handleSendMessage}
              disabled={(!input.trim() && attachments.length === 0)}
              aria-label="Send message"
            >
              <FaPaperPlane />
            </button>
          </div>
        </div>
      </div>
  
      {/* Move chat-history-sidebar to the right */}
      <div className={`chat-history-sidebar ${showHistory ? 'visible' : ''}`}>
        <div className="chat-history-header">
          <h2>Chat History</h2>
        </div>
        <button 
          className="new-chat-button"
          onClick={createNewChat}
        >
          <FaPlus /> New Chat
        </button>
        <div className="chat-history-list">
          {isLoadingHistory ? (
            <div className="loading-indicator">
              <FaSpinner className="spinner" /> Loading previous chats...
            </div>
          ) : historyError ? (
            <div className="no-chats-message">
              {historyError}
            </div>
          ) : !chatHistory || chatHistory.length === 0 ? (
            <div className="no-chats-message">
              No chat history yet. Start a new chat!
            </div>
          ) : (
            <VirtualScrollList
              items={chatHistory}
              itemHeight={80}
              containerHeight={400}
              overscan={5}
              hasMore={hasMoreChats}
              isLoading={isLoadingMoreChats}
              onLoadMore={loadMoreChats}
              className="virtual-chat-list"
              renderItem={(chat, index) => (
                <div
                  key={chat.id}
                  className={`chat-history-item ${selectedChat?.id === chat.id ? 'active' : ''}`}
                  onClick={() => {
                    setSelectedChat(chat);
                    setMessages([]);
                  }}
                >
                  <div className="chat-history-item-content">
                    <h3>{chat.lastMessage || 'New Chat'}</h3>
                  </div>
                  <button 
                    className="delete-chat-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteChat(chat.id);
                    }}
                  >
                    <FaTrash />
                  </button>
                </div>
              )}
            />
          )}
        </div>
      </div>
    </div>
  );
}


// Main Individual Plan App Component - This is what gets imported by App.js
function AgentVIndividualApp() {
  return <AgentVIndividualDashboard />;
}

export default AgentVIndividualApp; 